import { SelectionMode } from "@/types/SelectionManager";
import {
  AssignScopeCategory,
  AssignScopeElement,
  AssignScopesRequest,
  DeleteModelRequest,
  FeatureManagementType,
  Model,
  ModelScopeSettingsItemRespone,
  Permissions,
  SoftDeleteStatusType,
} from "@api/apiClient";

import { DownloadManagerEvents } from "@/types/DownloadManager/DownloadManager";
import { useAppSettings } from "@api/appSettings";
import { useCadModelCategories } from "@api/cadModelCategories";
import { useAssignPopulateModel, useAssignScopes, useDeleteModel, useModelNodes, useModelScopes, useModelUsageInfo } from "@api/models";
import MicroChip from "@components/MicroChip/MicroChip";
import useModelEdit from "@features/analysis/hooks/useModelEdit";
import { setSelectedNodesKeys, validateModelRow } from "@features/analysis/utils/treeGridUtils";
import useAppStore from "@hooks/useAppStore";
import useSelectionManager from "@hooks/useSelectionManager";
import useUserPermissionStore from "@hooks/useUserPermissionStore";
import i18n from "@i18n/i18n";
import CloseIcon from "@mui/icons-material/Close";
import { Button, Grid, IconButton, ListItemText, ListSubheader, Snackbar, Stack, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import isGuid from "@utils/isGuid";
import { useConfirm } from "material-ui-confirm";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import CreateVersionDialog from "./CreateVersionDialog";
import MuiModelGrid from "./MuiModelGrid";

const MuiModelTab = observer(() => {
  const { id: projectId } = useParams();
  const upStore = useUserPermissionStore();
  const [scopeId, setScopeId] = useState(0);
  const [openCreateVersionDialog, setOpenCreateVersionDialog] = useState(false);
  const modelGridApiRef = useGridApiRef();
  const appStore = useAppStore();
  const selectionManager = useSelectionManager();
  const appSettings = useAppSettings();
  const confirm = useConfirm();
  const cadModelCategories = useCadModelCategories();
  const modelScopes = useModelScopes(selectionManager.selectedModel?.modelId, { enabled: false });
  const mutateAssignScopes = useAssignScopes(selectionManager.selectedModel?.modelId, { enabled: false });
  const projectModel = useModelNodes(selectionManager.selectedModel?.modelId, { enabled: false });
  const modelUsageInfo = useModelUsageInfo(selectionManager.selectedModel?.modelId);
  const [openToast, setOpenToast] = useState(false);
  const [openSuccessToast, setOpenSuccessToast] = useState(false);
  const [openErrorToast, setOpenErrorToast] = useState(false);
  const [modifiedRows, setModifiedRows] = useState<Set<string>>(new Set());
  const queryClient = appStore.queryClient;
  const emitter = appStore.downloadManager.emitter;
  const modelId = selectionManager.selectedModel?.modelId;
  const handleDownloadDrained = useCallback(
    (event: unknown) => {
      const drainedEvent = event as { completedModelIds: string[] };

      if (modelId && drainedEvent.completedModelIds.includes(modelId)) {
        // This will cause it to read from OPFS instead of API
        queryClient.invalidateQueries({
          queryKey: ["modelNodes", modelId],
        });
      }
    },
    [modelId, queryClient],
  );

  // Subscribe to drained event when model is selected
  useEffect(() => {
    if (!modelId) return;

    emitter.on(DownloadManagerEvents.DOWNLOADS_DRAINED_EVENT, handleDownloadDrained);

    // Cleanup: unsubscribe when component unmounts or model changes
    return () => {
      emitter.off(DownloadManagerEvents.DOWNLOADS_DRAINED_EVENT, handleDownloadDrained);
    };
  }, [modelId, emitter, handleDownloadDrained]);

  const deleteModelMutation = useDeleteModel();
  const autoPopulateModelMutation = useAssignPopulateModel(selectionManager.selectedModel?.modelId);

  // Use the new useModelEdit hook
  const {
    handleCreateVersionSubmit,
    handleUpdateVersion,
    isCreatingVersion,
    isUpdatingVersion,
    isLoading: isModelEditLoading,
  } = useModelEdit({
    modelGridApiRef,
    modifiedRows,
    setModifiedRows,
  });

  const getSelectedNodes = () => {
    setSelectedNodesKeys(modelGridApiRef, appStore);
  };

  const selectModel = async (model: Model | null | undefined) => {
    if (model) {
      appStore.setSelectedNodesKeys(null);
      // Pass modelId for selection (selectModel accepts either modelId or URN)
      await selectionManager.selectModel(model.modelId!);
      // Clear modified rows when switching models
      setModifiedRows(new Set());

      await Promise.all([appStore.queryClient.resetQueries({ queryKey: ["materials"], exact: false })]);

      appStore.setAssignNodes(null);

      await modelScopes.refetch();
    }
  };

  const handleModelChange = (event: any) => {
    const model = selectionManager.selectedProject?.models!.find((m: Model) => m.modelId === event.target.value);
    if (model && model !== selectionManager.selectedModel) {
      selectModel(model);
    }
  };

  const handleAutoAssignScopes = async () => {
    getSelectedNodes();

    const selectedModelCategories = { ...appStore.selectedModelCategories };

    const categoryScopes = Object.keys(selectedModelCategories).map((key: string) => {
      return new AssignScopeCategory({
        cadModelCategoryId: appStore.selectedModelCategories[key].data.cadModelCategoryId,
        scopeId,
      });
    });
    const elementScopes = Object.keys(appStore.selectedNodesKeys)
      .filter((key: string) => isGuid(key))
      .reduce((acc: any, key: string) => {
        if (!Object.hasOwnProperty.call(appStore.selectedModelCategories, key)) {
          acc.push(
            new AssignScopeElement({
              elementId: key,
              scopeId,
            }),
          );
        }
        return acc;
      }, []);

    const assignScopesRequest = new AssignScopesRequest({
      modelId: selectionManager.selectedModel?.modelId,
      categoryScopes,
      elementScopes,
    });

    await mutateAssignScopes.mutateAsync(assignScopesRequest as any);
    await clearSelectedNodes();
    await modelScopes.refetch();
  };

  // Removed direct tree refresh; tree is rebuilt after downloads are drained

  const clearSelectedNodes = async () => {
    modelGridApiRef.current.setRowSelectionModel([]);
  };

  // Set selection mode on mount
  useEffect(() => {
    selectionManager.setSelectionMode(SelectionMode.SingleModel);
  }, [selectionManager]);

  const scopes = useMemo(() => {
    return appSettings.data?.modelScopeSetting?.modelScopeSettingsItems?.reduce((acc: any, item: ModelScopeSettingsItemRespone) => {
      if (item.deleteStatus === SoftDeleteStatusType.Active) {
        acc[item.id!] = item;
      }
      return acc;
    }, {});
  }, [appSettings.data]);

  const handleScopeChange = (event: any) => {
    setScopeId(event.target.value);
  };

  const handleCreateNewVersion = async () => {
    setOpenCreateVersionDialog(true);
  };

  const handleUpdateVersionClick = async () => {
    const rows = modelGridApiRef.current.getSortedRowIds().map((id) => modelGridApiRef.current.getRow(id));
    if (hasValidationErrors(rows)) {
      setOpenToast(true);
      return;
    }

    const success = await handleUpdateVersion();
    if (!success) {
      setOpenToast(true);
    }
  };

  const hasValidationErrors = (rows: any[]) => {
    return rows.some((row) => {
      const errors = validateModelRow(row);
      if (Object.keys(errors).length > 0) {
        return true;
      }
      return false;
    });
  };

  const handleCreateVersionSubmitClick = async (suffix: string) => {
    if (!selectionManager.selectedModel) {
      console.error("No model available for version creation");
      return;
    }

    const rows = modelGridApiRef.current.getSortedRowIds().map((id) => modelGridApiRef.current.getRow(id));
    if (hasValidationErrors(rows)) {
      setOpenToast(true);
      return;
    }

    const success = await handleCreateVersionSubmit(suffix, selectionManager.selectedModel.modelId!);
    if (success) {
      setOpenCreateVersionDialog(false);
    } else {
      setOpenToast(true);
    }
  };

  const handleCloseCreateVersionDialog = () => {
    setOpenCreateVersionDialog(false);
  };

  const handleDeleteModel = async () => {
    if (!selectionManager.selectedModel || !isCreatedVersion(selectionManager.selectedModel) || !projectId) return;

    // Confirm deletion
    const modelName = selectionManager.selectedModel?.modelName;
    const { confirmed } = await confirm({
      title: i18n.t("scope-tab.delete-model-dialog.header"),
      description: i18n.t("scope-tab.delete-model-dialog.message", `Are you sure you want to delete the model version "${modelName}"? This action cannot be undone.`),
      confirmationText: i18n.t("dialog.yes"),
      cancellationText: i18n.t("dialog.cancel"),
      dialogProps: {
        maxWidth: "xs",
      },
      confirmationButtonProps: {
        variant: "contained",
        color: "error",
      },
      cancellationButtonProps: {
        variant: "outlined",
      },
    });

    if (!confirmed) return;

    try {
      const request = new DeleteModelRequest({
        modelId: selectionManager.selectedModel.modelId!,
        projectId: projectId,
      });

      await deleteModelMutation.mutateAsync(request);
    } catch (error) {
      console.error("Error deleting model:", error);
    }
  };

  const handleAutoPopulate = async () => {
    if (!selectionManager.selectedModel?.modelId) return;

    try {
      await autoPopulateModelMutation.mutateAsync();
      
      // Clear the cached model data since server data has changed
      if (typeof window !== 'undefined' && (window as any).__modelDownloader) {
        await (window as any).__modelDownloader.invalidateModelCache(selectionManager.selectedModel.modelId);
      }
      
      // Force redownload the model data to get updated information from server
      appStore.downloadManager.enqueueLoad(selectionManager.selectedModel.modelId, 'high');
      
      // Show success message
      setOpenSuccessToast(true);
    } catch (error) {
      console.error("Error auto-populating model:", error);
      setOpenErrorToast(true);
    }
  };

  // Helper function to check if model is a created version (has suffix & version)
  const isCreatedVersion = (model: Model | null) => {
    return model && model.suffix && model.suffix.trim() && model.version;
  };

  return (
    <>
      <Box mt={1} sx={{ position: "relative", display: "flex", flexDirection: "column", minHeight: "100%", width: "100%" }}>
        <Stack direction={"row"} spacing={2}>
          <FormControl fullWidth>
            <InputLabel id="model-label">{i18n.t("scope-tab.models-select.title")}</InputLabel>
            <Select
              labelId="model-label"
              id="model-id"
              value={selectionManager.selectedModel?.modelId || ""}
              label={i18n.t("scope-tab.models-select.title")}
              onChange={handleModelChange}
            >
              <ListSubheader>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box>{i18n.t("scope-tab.models-select.title")}</Box>
                  <Typography color="text.secondary">{i18n.t("scope-tab.version.title")} </Typography>
                </Box>
              </ListSubheader>
              {selectionManager.selectedProject?.models
                ?.slice()
                ?.sort((a: Model, b: Model) => {
                  // Sort by creation date descending (latest first)
                  const dateA = new Date(a.createdAt || 0);
                  const dateB = new Date(b.createdAt || 0);
                  return dateB.getTime() - dateA.getTime();
                })
                ?.map((model: Model) => (
                  <MenuItem key={model.modelId} value={model.modelId}>
                    <ListItemText
                      sx={{
                        "& .MuiListItemText-primary": {
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        },
                      }}
                    >
                      <Box>{model?.modelName}</Box>
                      <MicroChip label={model?.modelVersion || ""} />
                    </ListItemText>
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Stack>
        <Box mt={2} sx={{ display: "flex", flexDirection: "column", flexGrow: 1, minWidth: "100px", width: "100%" }}>
          {selectionManager.selectedModel && (
            <>
              <MuiModelGrid modelId={selectionManager.selectedModel?.modelId} apiRef={modelGridApiRef} onRowModified={setModifiedRows} />
              <Grid my={1} container spacing={1} sx={{ alignItems: "center", justifyContent: "end" }}>
                {appStore.hasFeature(FeatureManagementType.ModelScopes) && (
                  <Grid>
                    <FormControl sx={{ minWidth: 200 }} size="small">
                      <InputLabel id="scope-label">{i18n.t("scope-tab.scope-select.title")}</InputLabel>
                      <Select labelId="scope-label" id="scope-id" value={scopeId} label={i18n.t("scope-tab.scope-select.title")} onChange={handleScopeChange} autoWidth>
                        <ListSubheader>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <Box>{i18n.t("scope-tab.scope-select.title")}</Box>
                          </Box>
                        </ListSubheader>
                        {Object.keys(scopes).map((id: any) => (
                          <MenuItem key={id} value={id}>
                            <ListItemText
                              sx={{
                                "& .MuiListItemText-primary": {
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                },
                              }}
                            >
                              <Box>{scopes[id].name}</Box>
                            </ListItemText>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                {appStore.hasFeature(FeatureManagementType.ModelScopes) && (
                  <Grid>
                    <Button
                      disabled={Object.keys(appStore.selectedNodesKeys || {}).length === 0 || !upStore.hasPermission(Permissions.App_Project_Model_Create)}
                      loading={!!mutateAssignScopes.isPending}
                      variant="contained"
                      onClick={handleAutoAssignScopes}
                    >
                      {i18n.t("scopes-tab.assign-button.title")}
                    </Button>
                  </Grid>
                )}
                <Grid>
                  <Button
                    disabled={!selectionManager.selectedModel || !upStore.hasPermission(Permissions.App_Project_Model_Create)}
                    loading={!!autoPopulateModelMutation.isPending}
                    variant="contained"
                    color="primary"
                    onClick={handleAutoPopulate}
                  >
                    Auto Populate
                  </Button>
                </Grid>
                <Grid>
                  <Button
                    disabled={
                      !isCreatedVersion(selectionManager.selectedModel) ||
                      !upStore.hasPermission(Permissions.App_Project_Model_Delete) ||
                      (modelUsageInfo.data ? !modelUsageInfo.data.isDeleteOrUpdateEligible : false)
                    }
                    loading={!!deleteModelMutation.isPending}
                    variant="outlined"
                    color="error"
                    onClick={handleDeleteModel}
                    title={
                      modelUsageInfo.data && !modelUsageInfo.data.isDeleteOrUpdateEligible
                        ? `Cannot delete: ${modelUsageInfo.data.usageDetails?.join(", ") || "Unknown reason"}`
                        : undefined
                    }
                  >
                    {i18n.t("scope-tab.delete-model-button.title")}
                  </Button>
                </Grid>
                {/* Conditional buttons based on whether model can be updated */}
                {isCreatedVersion(selectionManager.selectedModel) && modelUsageInfo.data?.isDeleteOrUpdateEligible === true ? (
                  <>
                    {/* Update button - direct action */}
                    <Grid>
                      <Button
                        disabled={!selectionManager.selectedModel || !upStore.hasPermission(Permissions.App_Project_Scenario_Create)}
                        loading={isUpdatingVersion}
                        variant="contained"
                        color="secondary"
                        onClick={handleUpdateVersionClick}
                      >
                        {i18n.t("scope-tab.update-version-button.title")}
                      </Button>
                    </Grid>
                    {/* Create button - opens dialog */}
                    <Grid>
                      <Button
                        disabled={!selectionManager.selectedModel || !upStore.hasPermission(Permissions.App_Project_Scenario_Create)}
                        variant="contained"
                        color="primary"
                        onClick={handleCreateNewVersion}
                      >
                        {i18n.t("scope-tab.create-new-version-button.title")}
                      </Button>
                    </Grid>
                  </>
                ) : (
                  /* Single Create button when not updatable */
                  <Grid>
                    <Button
                      disabled={!selectionManager.selectedModel || !upStore.hasPermission(Permissions.App_Project_Scenario_Create)}
                      variant="contained"
                      color="primary"
                      onClick={handleCreateNewVersion}
                    >
                      {i18n.t("scope-tab.create-new-version-button.title")}
                    </Button>
                  </Grid>
                )}
              </Grid>
            </>
          )}
        </Box>

        {/* Create Version Dialog */}
        <CreateVersionDialog
          open={openCreateVersionDialog}
          onClose={handleCloseCreateVersionDialog}
          onCreateVersion={handleCreateVersionSubmitClick}
          currentModel={selectionManager.selectedModel}
          projectModels={selectionManager.selectedProject?.models || []}
        />
      </Box>
      <Snackbar
        open={openToast}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        message={i18n.t("scope-tab.multiplier.snackbar-message")}
        onClose={() => setOpenToast(false)}
        ContentProps={{
          sx: {
            backgroundColor: "#d0ebff",
            color: "#003366",
          },
        }}
        action={
          <IconButton size="small" aria-label="close" color="inherit" onClick={() => setOpenToast(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
      <Snackbar
        open={openSuccessToast}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        message="Model populated successfully!"
        onClose={() => setOpenSuccessToast(false)}
        ContentProps={{
          sx: {
            backgroundColor: "#4caf50",
            color: "#ffffff",
          },
        }}
        action={
          <IconButton size="small" aria-label="close" color="inherit" onClick={() => setOpenSuccessToast(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
      <Snackbar
        open={openErrorToast}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        message="Error updating the model. Please try again."
        onClose={() => setOpenErrorToast(false)}
        ContentProps={{
          sx: {
            backgroundColor: "#f44336",
            color: "#ffffff",
          },
        }}
        action={
          <IconButton size="small" aria-label="close" color="inherit" onClick={() => setOpenErrorToast(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </>
  );
});

export default MuiModelTab;
